import React, { useState, useCallback, useRef } from 'react';
import { Box, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, NewPageTitleBar } from '@/components';
import FilterShopDateRange from '@/components/filters/FilterShopDateRange';
import { getMenuData } from '@core/data/menuData';
import AppCard from '@/components/appcard/AppCard';
import { hybridLocService } from '@/services/hybridLocService';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import WcnApexchart from '@/components/charts/WcnApexchart';
import { DateTime } from 'luxon';
import { FilterRef } from '@features/statistics/types.ts';
import { useNotifications } from '@core/hooks/useNotifications.ts';

interface FilterArgs {
    shopId: string;
    start: string;
    end: string;
}

const HybridLocDetailedUsage: FCSecurePage = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [series, setSeries] = useState<{ name: string; data: { x: string; y: number }[] }[]>([]);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const filterRef = useRef<FilterRef>(null);
    const { notifyError } = useNotifications();

    const loadData = useCallback(
        async (args: FilterArgs) => {
            setLoading(true);
            setSeries([]);

            try {
                const data = await hybridLocService.loadDetailedUsage(args.shopId, args.start, args.end);

                setSeries([
                    {
                        name: t('usage'),
                        data: data.map(({ date, count }) => ({ x: date, y: count }))
                    }
                ]);

                setLastUpdate(DateTime.now());
            } catch (error) {
                console.error('Failed to load HybridLoc detailed usage data:', error);
                notifyError(t('errorLoadingData'));
            } finally {
                setLoading(false);
            }
        },
        [t]
    );

    const handleReload = useCallback(async () => {
        filterRef.current?.refresh();
    }, []);

    useRegisterRefresh(handleReload);

    return (
        <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default' }}>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-lock" />}
                title={`${t('hybridLoc')} ${t('detailedUsage')}`}
                iconSize="18px"
                menus={getMenuData()}
                currentRoute={{ path: '/statistics/hybridloc/detailed-usage' }}
                lastUpdate={lastUpdate}
                t={t}
            />

            <Container maxWidth={false}>
                <FilterShopDateRange ref={filterRef} maxRange={Number.MAX_VALUE} onLoadData={loadData} />

                {loading ? (
                    <LoadingSpinner showBackground={false} />
                ) : (
                    <AppCard
                        heading={`${t('hybridLoc')} ${t('detailedUsage')}`}
                        icon="lock"
                        colClasses="flex col-12"
                        customClasses="mb-0"
                        fullBlock={false}
                        footer={false}
                    >
                        <WcnApexchart
                            type="line"
                            height={550}
                            chartId="HybridLocDetailedUsage"
                            series={series}
                            xAxisType="datetime"
                            yAxisMin={0}
                            yAxisForceNiceScale={true}
                            strokeCurve="straight"
                            strokeDashArray={[0]}
                            tooltipShared={false}
                        />
                    </AppCard>
                )}
            </Container>
        </Box>
    );
};

export default HybridLocDetailedUsage;
